import {
  AccountIcon,
  CalendarIcon,
  CopyIcon,
  InPersonFilled
} from '@blueprinthq/joy'
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react'
import { VideoCamera, Wait } from '@components/icons'
import { EVENT_SETTING_TELEHEALTH } from '@handlers/clinician-calendar/models'
import { useTelehealthLinks } from '@hooks'
import { copyToClipboard } from '@utilities'
import { capitalize } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useCalendarEvent } from '../context/calendar-event.context'

export const CalendarEventDetailBox = ({
  isCancelling = false
}: {
  isCancelling?: boolean
}) => {
  const { currentEvent, user } = useCalendarEvent()
  const { telehealthUrl } = useTelehealthLinks()
  const toast = useToast()

  const copyEventDetailsToClipboard = () => {
    copyToClipboard(`${moment(currentEvent?.start).format(
      'dddd, MMM D'
    )} ${moment(currentEvent?.start).format('h:mm A')}
${currentEvent?.getDuration()} minute ${currentEvent?.serviceType?.text}
With ${user.first_name} ${user.last_name}
${
  currentEvent?.setting === EVENT_SETTING_TELEHEALTH
    ? `Telehealth: ${telehealthUrl}`
    : 'In-person'
}`)
    toast({
      title: 'Event details copied to clipboard',
      status: 'success',
      duration: 3000,
      isClosable: true
    })
  }
  if (!currentEvent) {
    return null
  }

  return (
    <Flex
      w="100%"
      mt="small"
      p="small"
      direction="column"
      gap="xsmall"
      pos="relative"
      align="flex-start"
      justify="space-between"
      border="1px solid"
      borderColor="light_gray"
      borderRadius="8px"
    >
      <IconButton
        pos="absolute"
        top="4px"
        right="4px"
        background={'white'}
        _hover={{
          bg: '#E4E5E6'
        }}
        _focus={{
          outline: 'none'
        }}
        size="xs"
        aria-label="copy"
        onClick={copyEventDetailsToClipboard}
        variant="outline"
        style={{
          borderRadius: '4px'
        }}
      >
        <CopyIcon size="sm" />
      </IconButton>
      {isCancelling && (
        <Text fontSize="12px" color="#C6162C">
          This appointment has been canceled
        </Text>
      )}
      <HStack spacing="xsmall">
        <CalendarIcon size="sm" />
        <Text as="span" fontSize="12px" fontWeight="bold">
          {moment(currentEvent.start).format('dddd, MMM D')}
        </Text>
        <Text as="span" fontSize="12px">
          {moment(currentEvent.start).format('h:mm A')}
        </Text>
      </HStack>
      <HStack spacing="xsmall">
        <Wait fill="black" size={16} />
        <Text as="span" fontWeight="bold" fontSize="12px">
          {currentEvent.getDuration()} min
        </Text>
        <Text as="span" fontSize="12px">
          {currentEvent.serviceType?.text}
        </Text>
      </HStack>
      <HStack spacing="xsmall">
        <AccountIcon size="sm" />
        <Text as="span" fontWeight="bold" fontSize="12px">
          With
        </Text>
        <Text fontSize="12px">
          {user.first_name} {user.last_name}
        </Text>
      </HStack>
      {!isCancelling &&
        (currentEvent.setting === EVENT_SETTING_TELEHEALTH ? (
          <VStack spacing="4px" align="flex-start">
            <HStack spacing="xsmall">
              <VideoCamera width="16px" />
              <Text fontWeight="bold" fontSize="12px">
                {capitalize(currentEvent.setting)}
              </Text>
            </HStack>
            <HStack spacing="xsmall">
              <Box width="16px" />
              <Text color="gray" fontSize="12px">
                {telehealthUrl}
              </Text>
            </HStack>
          </VStack>
        ) : (
          <HStack spacing="xsmall">
            <InPersonFilled width="16px" height="16px" />
            <Text fontWeight="bold" fontSize="12px">
              {capitalize(currentEvent.setting)}
            </Text>
          </HStack>
        ))}
    </Flex>
  )
}
